<template>
  <main class="pb-16 pt-8 container">
    <base-form
      v-slot="{ reset }"
      aria-labelledby="location-selector-heading"
      class="mx-a space-y-4"
      :form
      style="max-width: 31rem"
    >
      <h1 id="location-selector-heading" class="title-1 c-black ">
        {{ $t.locationSelectorHeading }}
      </h1>
      <div>
        <p class="mb-1">
          {{ $t.locationSelectorCountryInstruction }}
        </p>
        <vf-form-field v-slot="{ attrs }" name="country" :rule="validateRequired($t.locationSelectorCountryError)">
          <vf-select
            v-model="form.country"
            v-bind="attrs"
            :options="countries"
            :placeholder="$t.locationSelectorCountryPlaceholder"
            required
            @update:model-value="reset"
          >
            {{ $t.locationSelectorCountry }}
          </vf-select>
        </vf-form-field>
      </div>
      <div v-if="availableLocales.length !== 1">
        <p class="mb-1">
          {{ $t.locationSelectorLanguageInstruction }}
        </p>
        <vf-form-field v-slot="{ attrs }" name="language" :rule="validateRequired($t.locationSelectorLanguageError)">
          <vf-select
            v-model="form.language"
            v-bind="attrs"
            :disabled="!form.country"
            :options="languages"
            :placeholder="$t.locationSelectorLanguagePlaceholder"
            required
          >
            {{ $t.locationSelectorLanguage }}
          </vf-select>
        </vf-form-field>
      </div>
      <p v-if="selectedLocale?.name" class="flex gap-1">
        <span class="text-md fw-bold">
          {{ $t.locationSelectorLanguage }}:
        </span>
        <span class="text-md">{{ selectedLocale.name }}</span>
      </p>
      <p v-if="selectedSite?.currency" class="flex gap-1">
        <span class="text-md fw-bold">
          {{ $t.locationSelectorCurrency }}:
        </span>
        <span class="text-md">{{ selectedSite.currency }}</span>
      </p>
      <vf-button
        class="w-full"
        :disabled="!redirectLink"
        referrerpolicy="no-referrer-when-downgrade"
        rel="noopener"
        target="_self"
        :to="redirectLink"
      >
        {{ $t.locationSelectorCta }}
      </vf-button>
      <p>
        {{ $t.locationSelectorNotice }}
      </p>
    </base-form>
  </main>
</template>

<script lang="ts" setup>
import type { SelectorLocale, SelectorSite } from '#types/locationSelector'

definePageMeta({
  disableLocationChecks: true
})

const { data } = await useApi().internal.getStaticPage('location-selector')
const { $feature, $t } = useNuxtApp()
const locale = useLocale()

/*
We have the ability to add a "flag" attribute to the country object in location-selector.json.
The value of the "flag" attribute should be the feature flag that controls whether or not this country will show in the location selector.
*/
const availableCountries = data.value?.filter(({ flag }) => flag === undefined || $feature[flag] === true) || []

const defaults = availableCountries.reduce((acc, cur) => {
  if (acc.country) return acc
  const loc = cur.locales.find(({ code }) => code === locale)
  if (loc) {
    acc.country = cur.name
    acc.language = loc.name
  }
  return acc
}, {})

const form = reactive(defaults)

const countries = availableCountries.map(({ name }) => name)

const selectedSite = computed<SelectorSite | undefined>(
  () => availableCountries.find(({ name }) => name === form.country)
)

/*
We have the ability to add a "flag" attribute to the locale object in location-selector.json.
The value of the "flag" attribute should be the feature flag that controls whether or not this locale will show in the location selector.
*/
const availableLocales = computed<SelectorLocale[]>(() => selectedSite.value?.locales
  .filter(({ flag }) => flag === undefined || $feature[flag] === true) || [])

const selectedLocale = computed<SelectorLocale | undefined>(
  () => availableLocales.value.length > 1
    ? availableLocales.value.find(({ name }) => name === form.language)
    : availableLocales.value[0]
)

const languages = computed(() => availableLocales.value.map(({ name }) => name) || [])

const redirectLink = computed(() => {
  if (!selectedSite.value || !selectedLocale.value) return ''
  if (selectedSite.value.canvasDomain && $feature.configStaticPagesSitemaps.includes(selectedLocale.value.code)) {
    if (!useSubdomain()) return `${useHost()}/${selectedLocale.value.code}`
    return `${selectedSite.value.canvasDomain.replace('{subdomain}', useSubdomain())}/${selectedLocale.value.code}`
  }
  return `${selectedSite.value.domain}/${selectedLocale.value.path}`
})
</script>
